<template>
  <div part="base" class="sp-rsvp-invite-modal">
    <sp-modal
      :modal-title="`Invite ${store.labelPlural}`"
      width="44rem"
      :open="store.isOpen"
      @update-open="handleUpdateOpen"
      @close="store.close()"
    >
      <sp-card-content-overlay slot="content-overlay" :visible="store.isSubmitting">
        <sp-progress type="ellipsis" size="x-small"></sp-progress>
      </sp-card-content-overlay>

      <InputStep v-if="store.currentStep === 'input'" key="input" />
      <ConfirmStep v-else key="confirm" />

      <ModalActions slot="actions" @manual-add="handleManualAdd" />
    </sp-modal>
  </div>
</template>

<script setup>
import { nextTick, onMounted, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { toBoolean, toNumber } from "#ce/utils/props";
import InputStep from "./components/InputStep.ce.vue";
import ConfirmStep from "./components/ConfirmStep.ce.vue";
import ModalActions from "./components/ModalActions.ce.vue";
import { props as appProps } from "./props.js";
import { useRsvpInviteStore } from "./store/rsvpInvite";

const emit = defineEmits(["rsvp-invite-complete"]);

const props = defineProps(appProps);

const store = useRsvpInviteStore();
const { exposeMethods } = useExpose();

function buildConfig(overrides = {}) {
  return {
    eventId: toNumber(props.eventId, 0),
    previewUrl: props.previewUrl,
    processUrl: props.processUrl,
    addAttendeeUrl: props.addAttendeeUrl,
    label: props.label,
    emailInputLabel: props.emailInputLabel,
    dropzoneText: props.dropzoneText,
    fileSpecHtml: props.fileSpecHtml,
    ...overrides,
  };
}

function open(overrides) {
  store.configure(buildConfig(overrides));
  store.open();
}

function close() {
  store.close();
}

exposeMethods({ open, close });

watch(
  () => store.completedCount,
  (value, oldValue) => {
    if (value > (oldValue ?? 0)) {
      emit("rsvp-invite-complete");
    }
  },
);

onMounted(() => {
  store.configure(buildConfig());

  if (toBoolean(props.autoOpen)) {
    nextTick(() => store.open());
  }
});

function handleUpdateOpen({ detail }) {
  const [value] = detail;

  if (value) {
    store.open();
    return;
  }

  close();
}

function handleManualAdd() {
  if (!store.config.addAttendeeUrl) {
    return;
  }

  close();

  const link = document.createElement("a");
  link.href = store.config.addAttendeeUrl;
  link.dataset.behaviour = "new-action-modal";
  link.dataset.title = `Add ${store.config.label}`;
  link.dataset.modalModifierEscOut = "false";
  document.body.appendChild(link);
  link.click();
  link.remove();
}
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped>
.sp-rsvp-invite-modal {
  display: block;

  --sp-comp-card-padding-block: 1rem;
  --sp-card-actions-padding-inline: 0;
  --sp-card-actions-padding-block: 0;
}
</style>
